/**
 * API endpoint paths for the ERP application
 * Updated for the real ERP API structure
 */

export const endpoints = {
  // Authentication endpoints
  auth: {
    login: '/api/ApiAuthentication/Login',
    userDetails: (username: string) => `/api/ApiAuthentication/UserDetailsByUsername/${encodeURIComponent(username)}`,
  },

  // Dashboard endpoints
  dashboards: {
    finance: (branchId: number) => `/api/Dashbard/GetFinanceDashboard?branchId=${branchId}`,
    hr: (branchId: number) => `/api/Dashboard/GetHRDashboard?branchId=${branchId}`,
    receivables: (branchId: number) => `/api/Dashbard/GetReceivables/${branchId}`,
    payables: (branchId: number) => `/api/Dashbard/GetPayables/${branchId}`,
    incomeExpense: (branchId: number, year: number, isAccural: boolean) => 
      `/api/Dashbard/GetIncomeExpense/${branchId}?year=${year}&isAccural=${isAccural}`,
    cashFlow: (branchId: number, year: number) => 
      `/api/Dashbard/GetCashFlow/${branchId}?year=${year}`,
    profitLoss: (branchId: number, year: number) => 
      `/api/Dashbard/GetProfitLoss/${branchId}?year=${year}`,
  },

  // Employee management endpoints
  employees: {
    getAll: (branchId: number, pageNumber: number, pageSize: number) => 
      `/api/Employee/GetAllEmployee/${branchId}/${pageNumber}/${pageSize}`,
    getById: (branchId: number, id: number) => 
      `/api/Employee/GetemployeeById/${branchId}/${id}`,
    save: '/api/Employee/SaveEmployee',
    update: '/api/Employee/UpdateEmployee',
    delete: (id: number) => `/api/Employee/DeleteEmployee/${id}`,
  },

  // Employee Address endpoints
  empAddress: {
    getAll: (branchId: number, pageNumber: number, pageSize: number) =>
      `/api/EmpAddress/GetAllEmployeeAddress/${branchId}/${pageNumber}/${pageSize}`,
    getById: (id: number) => `/api/EmpAddress/GetEmployeeAddressById/${id}`,
    getByEmpId: (empId: number) => `/api/EmpAddress/GetEmployeeAddressByEmpId/${empId}`,
    create: '/api/EmpAddress/CreateEmployeeAddress',
    update: '/api/EmpAddress/UpdateEmployeeAddress',
    delete: (id: number) => `/api/EmpAddress/DeleteEmployeeAddress/${id}`,
  },

  // EmpDocument
  empDocument: {
    getList: (branchId: number, pageNumber: number, pageSize: number) =>
      `/api/EmpDocument/GetDocuments/${branchId}/${pageNumber}/${pageSize}`,
    getById: (id: number) => `/api/EmpDocument/GetDocumentById/${id}`,
    getByEmpId: (empId: number) => `/api/EmpDocument/GetDocumentsByEmpId/${empId}`,
    getDocumentTypes: '/api/EmpDocument/GetDocumentTypes',
    add: '/api/EmpDocument/AddDocument',
    update: (documentId: number) => `/api/EmpDocument/UpdateDocument/${documentId}`,
    delete: (documentId: number) => `/api/EmpDocument/DeleteDocument/${documentId}`,
  },

  // EmpQualification (GetQualificationTypes, GetAll: branchId query; GetById/Delete: id query)
  empQualification: {
    getQualificationTypes: '/api/EmpQualification/GetQualificationTypes',
    getAll: (branchId: number, pageNumber: number, pageSize: number) =>
      `/api/EmpQualification/GetAll?branchId=${branchId}&pageNumber=${pageNumber}&pageSize=${pageSize}`,
    getById: (id: number) => `/api/EmpQualification/GetById?id=${id}`,
    getByEmpId: (empId: number) => `/api/EmpQualification/GetByEmpId/${empId}`,
    create: '/api/EmpQualification/Create',
    update: '/api/EmpQualification/Update',
    delete: (id: number) => `/api/EmpQualification/Delete?id=${id}`,
  },

  // EmpSkill (GetAll: branchId query; GetById/Delete: id query; GetByEmpId: empId query)
  empSkill: {
    getSkillTypes: '/api/EmpSkill/GetSkillTypes',
    // Backend expects pagination params (Swagger: pageNumber/pageSize)
    getAll: (branchId: number, pageNumber: number, pageSize: number) =>
      `/api/EmpSkill/GetAll?branchId=${branchId}&pageNumber=${pageNumber}&pageSize=${pageSize}`,
    getById: (id: number) => `/api/EmpSkill/GetById?id=${id}`,
    getByEmpId: (empId: number) => `/api/EmpSkill/GetByEmpId?empId=${empId}`,
    save: '/api/EmpSkill/Save',
    update: '/api/EmpSkill/Update',
    delete: (id: number) => `/api/EmpSkill/Delete?id=${id}`,
  },

  // Training (GetAll: branchId query; GetById/Delete: id query; GetByEmpId: empId query)
  training: {
    // Swagger expects pagination params too (pageNumber/pageSize)
    getAll: (branchId: number, pageNumber: number, pageSize: number) =>
      `/api/Training/GetAll?branchId=${branchId}&pageNumber=${pageNumber}&pageSize=${pageSize}`,
    getById: (id: number) => `/api/Training/GetById?id=${id}`,
    getByEmpId: (empId: number) => `/api/Training/GetByEmpId?empId=${empId}`,
    save: '/api/Training/Save',
    update: '/api/Training/Update',
    delete: (id: number) => `/api/Training/Delete?id=${id}`,
  },

  // Vehicle (GetVehicleTypes, GetAll: branchId query; GetById/Delete: id query; GetByEmpId: empId query)
  vehicle: {
    getVehicleTypes: '/api/Vehicle/GetVehicleTypes',
    // Swagger expects pagination params too
    getAll: (branchId: number, pageNumber: number, pageSize: number) =>
      `/api/Vehicle/GetAll?branchId=${branchId}&pageNumber=${pageNumber}&pageSize=${pageSize}`,
    getById: (id: number) => `/api/Vehicle/GetById?id=${id}`,
    getByEmpId: (empId: number) => `/api/Vehicle/GetByEmpId?empId=${empId}`,
    save: '/api/Vehicle/Save',
    update: '/api/Vehicle/Update',
    delete: (id: number) => `/api/Vehicle/Delete?id=${id}`,
  },

  // EmpMedical (GetMedicalCompanies, GetMedicalClasses, GetAll with pagination; GetById/Delete: id query; GetByEmployeeId: employeeId query)
  empMedical: {
    getMedicalCompanies: '/api/EmpMedical/GetMedicalCompanies',
    getMedicalClasses: '/api/EmpMedical/GetMedicalClasses',
    getAll: (branchId: number, pageNumber: number, pageSize: number) =>
      `/api/EmpMedical/GetAll?branchId=${branchId}&pageNumber=${pageNumber}&pageSize=${pageSize}`,
    getById: (id: number) => `/api/EmpMedical/GetById?id=${id}`,
    getByEmployeeId: (employeeId: number) => `/api/EmpMedical/GetByEmployeeId?employeeId=${employeeId}`,
    save: '/api/EmpMedical/Save',
    update: '/api/EmpMedical/Update',
    delete: (id: number) => `/api/EmpMedical/Delete?id=${id}`,
  },

  // EmpMediaLinks (GetAll: query params; GetById/Delete/GetByEmployeeId: path params)
  empMediaLinks: {
    getAll: (branchId: number, pageNumber: number, pageSize: number) =>
      `/api/EmpMediaLinks/GetAll?branchId=${branchId}&pageNumber=${pageNumber}&pageSize=${pageSize}`,
    getById: (id: number) => `/api/EmpMediaLinks/GetById/${id}`,
    getByEmployeeId: (employeeId: number) => `/api/EmpMediaLinks/GetByEmployeeId/${employeeId}`,
    save: '/api/EmpMediaLinks/Save',
    update: '/api/EmpMediaLinks/Update',
    delete: (id: number) => `/api/EmpMediaLinks/Delete/${id}`,
  },

  // Employee lookup endpoints
  employeeLookups: {
    gender: '/api/Employee/GetGenderLookups',
    departments: '/api/Employee/GetDepartment',
    countries: '/api/Employee/GetCountryLookups',
    cities: '/api/Employee/GetCityLookups',
    professionIqama: '/api/Employee/GetProfessioninIqamaLookups',
    noticePeriod: '/api/Employee/GetNoticePeriodLookups',
    maritalStatus: '/api/Employee/GetMaritalStatusLookups',
    jobs: '/api/Employee/GetJobLookups',
    status: '/api/Employee/GetStatusLookups',
  },

  // Contract management endpoints
  contracts: {
    getAll: (branchId: number, pageNumber: number, pageSize: number) =>
      `/api/Contract/GetAllcontract/${branchId}/${pageNumber}/${pageSize}`,
    getById: (id: number, branchId: number) =>
      `/api/Contract/GetcontractById/${id}/${branchId}`,
    getAllEmployees: (branchId: number) =>
      `/api/Contract/GetAllEmployee${branchId}`,
    getDepartments: (branchId: number) =>
      `/api/Contract/GetDepartment/${branchId}`,
    getCurrency: '/api/Contract/GetCurrency',
    getSalaryType: '/api/Contract/GetSalaryType',
    getGrade: '/api/Contract/GetGrade',
    getSection: '/api/Contract/GetSection',
    save: '/api/Contract/SaveContract',
    update: '/api/Contract/UpdateContract',
    renew: '/api/Contract/RenewContract',
  },

  // Salary endpoints
  salary: {
    getByEmployeeId: (empId: number, contractId: number) =>
      `/api/Salary/GetSalariesByEmployeeId?empId=${empId}&contractId=${contractId}`,
  },

  // Attendance (Payroll)
  attendance: {
    addCombined: '/api/Attendance/AddCombinedAttendanceAsync',
    addSingle: '/api/Attendance/AddAttendanceAsync',
    getList: (branchId: number, pageNumber: number, pageSize: number) =>
      `/api/Attendance/GetAttendance/${branchId}/${pageNumber}/${pageSize}`,
    getByDepartmentEmployeeDateRange: (
      departmentId: number,
      empId: number,
      dateFrom: string,
      dateTo: string,
      pageNumber: number,
      pageSize: number,
      branchId: number
    ) => {
      const params = new URLSearchParams({
        departmentId: String(departmentId),
        empId: String(empId),
        dateFrom,
        dateTo,
        pageNumber: String(pageNumber),
        pageSize: String(pageSize),
        branchId: String(branchId),
      })
      return `/api/Attendance/GetAttendanceByDepartmentEmployeeDateRange?${params.toString()}`
    },
    getByMonthYear: (monthYear: string, branchId: number, pageNumber = 1, pageSize = 50) => {
      const mmYyyy = String(monthYear).replace(/\s/g, '-').trim()
      const q = `monthYear=${encodeURIComponent(mmYyyy)}&pageNumber=${pageNumber}&pageSize=${pageSize}&branchId=${branchId}`
      return `/api/Attendance/GetAttendanceByMonthYear?${q}`
    },
    update: '/api/Attendance/Update',
    post: (dateFrom: string, dateTo: string, departmentId: number, branchId: number) => {
      const params = new URLSearchParams({
        dateFrom,
        dateTo,
        departmentId: String(departmentId),
        branchId: String(branchId),
      })
      return `/api/Attendance/PostAttendanceAsync?${params.toString()}`
    },
  },

  // Incentive (Payroll)
  incentive: {
    getAll: (branchId: number, pageNumber: number, pageSize: number) =>
      `/api/Incentive/GetAllIncentives/${branchId}/${pageNumber}/${pageSize}`,
    getById: (id: number) => `/api/Incentive/${id}`,
    getDepartments: (branchId: number) =>
      `/api/Incentive/GetDepartment/${branchId}`,
    getEmployees: (branchId: number) =>
      `/api/Incentive/GetEmployee/${branchId}`,
    post: '/api/Incentive',
    put: '/api/Incentive',
    delete: (id: number) => `/api/Incentive/${id}`,
  },

  // Award Bonus (Payroll)
  awardBonus: {
    getAll: (branchId: number, pageNumber: number, pageSize: number) =>
      `/api/AwardBonus/GetAll/${branchId}/${pageNumber}/${pageSize}`,
    getById: (id: number, branchId: number) =>
      `/api/AwardBonus/${id}/${branchId}`,
    save: '/api/AwardBonus/Save',
    update: '/api/AwardBonus/Update',
    delete: (id: number) => `/api/AwardBonus/${id}`,
  },

  // Payroll Payment (list, get by id, get payment amount, create, update, delete)
  payrollPayment: {
    getAll: (branchId: number, pageNumber: number, pageSize: number) =>
      `/api/PayrollPayment/GetAllPayrollPayments/${branchId}/${pageNumber}/${pageSize}`,
    getById: (id: number) => `/api/PayrollPayment/GetPayrollPaymentById/${id}`,
    getLinesByPaymentId: (id: number) => `/api/PayrollPayment/GetPayrollPaymentLinesByPaymentId/${id}`,
    getByMonthYear: (monthYear: string, branchId: number) =>
      `/api/PayrollPayment/GetPayrollPaymentByMonthYear/${monthYear}/${branchId}`,
    getPaymentAmount: (monthYear: string, departmentId: number, branchId: number) => {
      const params = new URLSearchParams({
        monthYear,
        departmentId: String(departmentId),
        branchId: String(branchId),
      })
      return `/api/PayrollPayment/GetPaymentAmount?${params.toString()}`
    },
    create: '/api/PayrollPayment/CreatePayrollPayment',
    update: '/api/PayrollPayment/UpdatePayrollPayment',
    delete: (id: number) => `/api/PayrollPayment/DeletePayrollPayment/${id}`,
  },

  // Generate Payroll (Salary by month, Test, Post)
  payroll: {
    getSalaryByMonth: (branchId: number, monthYear: string, pageNumber: number, pageSize: number) => {
      const params = new URLSearchParams({ monthYear, pageNumber: String(pageNumber), pageSize: String(pageSize) })
      return `/api/Payroll/GetSalaryByMonth/${branchId}?${params.toString()}`
    },
    getSalaryById: (id: number) => `/api/Payroll/GetSalaryById/${id}`,
    getTest: (params: { monthYear: string; startDate: string; endDate: string; branchId: number; empId?: number; pageNumber: number; pageSize: number }) => {
      const q = new URLSearchParams({
        monthYear: params.monthYear,
        startDate: params.startDate,
        endDate: params.endDate,
        branchId: String(params.branchId),
        empId: String(params.empId ?? 0),
        pageNumber: String(params.pageNumber),
        pageSize: String(params.pageSize),
      })
      return `/api/Payroll/GetTest?${q.toString()}`
    },
    generatePayroll: '/api/Payroll/GeneratePayroll',
    getReport: (params: { branchId: number; fromMonth: string; toMonth: string; pageNumber: number; pageSize: number }) => {
      const q = new URLSearchParams({
        branchId: String(params.branchId),
        fromMonth: params.fromMonth,
        toMonth: params.toMonth,
        pageNumber: String(params.pageNumber),
        pageSize: String(params.pageSize),
      })
      return `/api/Payroll/GetReport?${q.toString()}`
    },
  },

  // Shift endpoints (HR Settings)
  apiShift: {
    getAll: (branchId: number, pageNumber: number, pageSize: number) =>
      `/api/ApiShift/GetAll/${branchId}/${pageNumber}/${pageSize}`,
    getById: (id: number, branchId: number) =>
      `/api/ApiShift/GetById/${id}/${branchId}`,
    create: '/api/ApiShift/Create',
    update: '/api/ApiShift/Update',
    delete: (id: number) => `/api/ApiShift/Delete/${id}`,
  },

  // Bank management endpoints
  banks: {
    getAll: (branchId?: number) => {
      let url = '/api/Bank/GetAll'
      if (branchId) {
        url += `?branchId=${branchId}`
      }
      return url
    },
    getById: (id: number) => `/api/Bank/GetById?id=${id}`,
    getByEmpId: (empId: number) => `/api/Bank/GetByEmpId?empId=${empId}`,
    save: '/api/Bank/Save',
    update: '/api/Bank/Update',
    delete: (id: number) => `/api/Bank/Delete?id=${id}`,
  },

  // Customer management endpoints (real ERP API)
  customers: {
    getAll: (branchId: number, pageNumber: number, pageSize: number) =>
      `/api/ApiCustomer/GetAllCustomers/${branchId}/${pageNumber}/${pageSize}`,
    getById: (branchId: number, id: number) =>
      `/api/ApiCustomer/GetCustomerById/${branchId}/${id}`,
    save: '/api/ApiCustomer/SaveCustomer',
    update: '/api/ApiCustomer/UpdateCustomer',
    delete: (id: number, branchId: number, ipAddress?: string) => {
      let url = `/api/ApiCustomer/DeleteCustomer/${id}?branchId=${branchId}`
      if (ipAddress) {
        url += `&ipAddress=${encodeURIComponent(ipAddress)}`
      }
      return url
    },
    getCountries: '/api/ApiCustomer/GetCountryLookups',
    getCities: (countryId?: number) => {
      const base = '/api/ApiCustomer/GetCityLookups'
      if (countryId !== undefined) {
        return `${base}?countryId=${countryId}`
      }
      return base
    },
    getStates: '/api/ApiBranch/GetStateLookups',
  },

  // Finance & sales shared endpoints
  finance: {
    customers: (branchId: number) => `/api/ApiInvoice/GetAllCustomers/${branchId}`,
    projects: (branchId: number, customerId: number) => `/api/ApiInvoice/GetProjects/${branchId}/${customerId}`,
    items: (branchId: number) => `/api/ApiInvoice/GetItemList?branchId=${branchId}`,
    taxRates: (branchId: number) => `/api/ApiInvoice/GetTaxRates/${branchId}`,
    paymentTerms: '/api/ApiInvoice/GetlookuppaymentTerms',
    currencies: '/api/ApiInvoice/GetAllcurrencies',
    invoiceCategories: '/api/ApiInvoice/GetlookupinvoiceCateogries',
    // ApiBills endpoints for General Entry
    getAllAccounts: (branchId: number) => `/api/ApiBills/GetAllAccounts/${branchId}`,
    getProject: (branchId: number) => `/api/ApiBills/GetProject/${branchId}`,
    getProfitCenter: (branchId?: number) => {
      const base = `/api/ApiBills/GetProfitCenter`
      if (branchId !== undefined) {
        return `${base}?branchId=${branchId}`
      }
      return base
    },
    getCostLocation: (branchId?: number) => {
      const base = `/api/ApiBills/GetCostLocation`
      if (branchId !== undefined) {
        return `${base}?branchId=${branchId}`
      }
      return base
    },
    getAllVendors: (branchId: number) => `/api/ApiBills/GetAllVendors/${branchId}`,
  },

  // Quotation management endpoints (real ERP API)
  quotations: {
    getAll: (branchId: number, pageNumber: number, pageSize: number) =>
      `/api/ApiQuotation/GetAllQuotation/${branchId}/${pageNumber}/${pageSize}`,
    byCustomer: (branchId: number, customerId?: number) => {
      const base = `/api/ApiQuotation/GetQuotationByCustomer/${branchId}`
      if (typeof customerId === 'number' && !Number.isNaN(customerId)) {
        return `${base}?CustomerId=${customerId}`
      }
      return base
    },
    getById: (branchId: number, id: number) =>
      `/api/ApiQuotation/GetQuotationById/${branchId}/${id}`,
    getLinesById: (branchId: number, id: number) =>
      `/api/ApiQuotation/GetQuotationLinesById/${branchId}?id=${id}`,
    insert: '/api/ApiQuotation/Insert',
    update: '/api/ApiQuotation/Update',
    convertToInvoice: (id: number, ipAddress?: string) => {
      let url = `/api/ApiQuotation/ConvertToInvoice?id=${id}`
      if (ipAddress) {
        url += `&ipAddress=${encodeURIComponent(ipAddress)}`
      }
      return url
    },
  },

  // Invoice management endpoints (real ERP API)
  invoices: {
    getAll: (branchId: number, pageNumber: number, pageSize: number) =>
      `/api/ApiInvoice/GetAllInvoiceList/${branchId}/${pageNumber}/${pageSize}`,
    getById: (id: number, branchId: number) =>
      `/api/ApiInvoice/GetInvoiceById/${id}/${branchId}`,
    getLines: (branchId: number) => `/api/ApiInvoice/GetAllInvoiceLines/${branchId}`,
    getLinesById: (id: number, branchId: number) =>
      `/api/ApiInvoice/GetAllInvoiceLinesbyId/${id}/${branchId}`,
    insert: '/api/ApiInvoice/Insert',
    update: '/api/ApiInvoice/Update',
    postData: (id: number) => `/api/ApiInvoice/PostData?id=${id}`,
  },

  // Credit Notes management endpoints (real ERP API)
  creditNotes: {
    getAll: (branchId: number, pageNumber: number, pageSize: number) =>
      `/api/ApiCreditNotes/GetAllCreditNotes/${branchId}/${pageNumber}/${pageSize}`,
    getById: (branchId: number, id: number) =>
      `/api/ApiCreditNotes/GetCreditNoteLineById/${branchId}/${id}`,
    getAllInvoices: (branchId: number, customerId?: number) => {
      let url = `/api/ApiCreditNotes/GetAllInvoice/${branchId}`
      if (customerId && customerId > 0) {
        url += `?customerId=${customerId}`
      }
      return url
    },
    getAllInvoiceLines: (branchId: number) =>
      `/api/ApiCreditNotes/GetAllInvoiceLines/${branchId}`,
    getInvLines: (id: number, branchId: number) => {
      return `/api/ApiCreditNotes/GetInvLines/${id}/${branchId}`
    },
    insert: '/api/ApiCreditNotes/Insert',
    update: '/api/ApiCreditNotes/Update',
    postData: (id: number, ipAddress?: string) => {
      let url = `/api/ApiCreditNotes/PostData?id=${id}`
      if (ipAddress) {
        url += `&IpAddress=${encodeURIComponent(ipAddress)}`
      }
      return url
    },
  },

  // Projects management endpoints
  projects: {
    getAll: (branchId: number, pageNumber: number, pageSize: number) =>
      `/api/ApiProject/GetProjects/${branchId}/${pageNumber}/${pageSize}`,
    getById: (id: number, branchId: number) =>
      `/api/ApiProject/GetProject/${id}/${branchId}`,
    getCostLocation: '/api/ApiProject/GetCostLocation',
    getProfitCenter: '/api/ApiProject/GetProfitCenter',
    save: '/api/ApiProject/SaveProject',
    update: '/api/ApiProject/UpdateProject',
  },

  // Account management endpoints (Chart of Accounts)
  accounts: {
    getAll: (branchId: number, pageNumber: number, pageSize: number) =>
      `/api/ApiAccount/GetAllAccount/${branchId}/${pageNumber}/${pageSize}`,
    getById: (id: number, branchId: number) =>
      `/api/ApiAccount/GetAccountById/${id}/${branchId}`,
    getByParentId: (id: number, branchId: number) =>
      `/api/ApiAccount/GetAccountsByParentId/${id}/${branchId}`,
    getTransactionableAccounts: (branchId: number) =>
      `/api/ApiAccount/GetTransactionableAccounts/${branchId}`,
    generateCode: (parentId: number) =>
      `/api/ApiAccount/GenerateAccountCode/${parentId}`,
    save: '/api/ApiAccount/SaveAccount',
    update: '/api/ApiAccount/UpdateAccount',
    uploadExcel: (branchId: number) =>
      `/api/ApiAccount/UploadExcel/${branchId}`,
    downloadChart: (branchId: number) =>
      `/api/ApiAccount/DownloadChartOfAccounts/${branchId}`,
  },

  // Lookup/Reference data endpoints
  lookups: {
    departments: '/lookups/departments',     // GET - List of departments
    positions: '/lookups/positions',         // GET - List of positions
    countries: '/lookups/countries',         // GET - List of countries
    currencies: '/lookups/currencies',       // GET - List of currencies
    statuses: '/lookups/statuses',          // GET - Various status lists
  },

  // Inbox / Workflow - Task Inbox list & detail
  inboxTasks: {
    taskNames: '/api/InboxTasks/TaskNames',
    approvalStatuses: '/api/InboxTasks/ApprovalStatuses',
    getAll: (taskId: number, statusId: number, branchId: number, pageNumber: number, pageSize: number) =>
      `/api/InboxTasks/GetAll?taskId=${taskId}&statusId=${statusId}&branchId=${branchId}&pageNumber=${pageNumber}&pageSize=${pageSize}`,
    getById: (id: number) => `/api/InboxTasks/GetById/${id}`,
    getNotifications: (branchId: number) => `/api/InboxTasks/GetNotifications?branchId=${branchId}`,
  },

  // Permissions (Security — modules & menus for role permissions grid)
  permissions: {
    getModules: '/api/Permissions/GetModules',
    /** Logged-in user's permission for a module/menu in a branch (omit menuName when empty = module-level only). */
    getPermissionForLoggedInUser: (
      moduleName: string,
      menuName: string,
      branchId: number,
      roleId: number
    ) => {
      const q = new URLSearchParams()
      q.set('moduleName', moduleName)
      q.set('branchId', String(branchId))
      q.set('roleId', String(roleId))
      const menu = menuName.trim()
      if (menu) q.set('menuName', menu)
      return `/api/Permissions/GetPermissionForLoggedInUser?${q.toString()}`
    },
    getMenu: (moduleName: string, roleId?: number) => {
      const base = `/api/Permissions/GetMenu?moduleName=${encodeURIComponent(moduleName)}`
      if (roleId != null && roleId > 0) return `${base}&roleId=${roleId}`
      return base
    },
    getPermissions: (roleId: number, moduleId: number, branchId: number) =>
      `/api/Permissions/GetPermissions?roleId=${roleId}&moduleId=${moduleId}&branchId=${branchId}`,
    save: '/api/Permissions/FormSave',
  },

  // Roles (Security)
  roles: {
    getAll: (branchId: number, pageNo: number, pageSize: number) =>
      `/api/Roles/GetAll?pageNo=${pageNo}&pageSize=${pageSize}&branchId=${branchId}`,
    getById: (id: number) => `/api/Roles/GetById?id=${id}`,
    save: '/api/Roles/Save',
    update: '/api/Roles/Update',
  },

  // User (for Requested By -> empId -> employee name)
  user: {
    getById: (branchId: number, id: number) => `/api/User/GetUserById/${branchId}/${id}`,
    getAll: (branchId: number, pageNumber: number, pageSize: number) =>
      `/api/User/GetAllUsers/${branchId}/${pageNumber}/${pageSize}`,
    getAllRoles: (branchId: number) => `/api/User/GetAllRoles/${branchId}`,
    getAllEmployees: (branchId: number) => `/api/User/GetAllEmployees/${branchId}`,
    create: '/api/User/CreateUser',
    update: '/api/User/UpdateUser',
  },

  // Approval route detail (approvers list, submit approval)
  approvalRouteDetail: {
    getApprovalUsers: (branchId: number, taskId: number) =>
      `/api/ApprovalRouteDetail/GetApprovalUsers?branchId=${branchId}&taskId=${taskId}`,
    approveRequest: (id: number, approvalNote: string, approvalStatusId: number) =>
      `/api/ApprovalRouteDetail/ApproveRequest?id=${id}&approvalNote=${encodeURIComponent(approvalNote)}&approvalStatusId=${approvalStatusId}`,
  },

  // Approval Template (Workflow Approval Master)
  approvalTemplate: {
    getUsers: (branchId: number, departmentId: number) =>
      `/api/ApprovalTemplate/GetUsers?branchId=${branchId}&departmentId=${departmentId}`,
    getByTaskId: (taskId: number, branchId: number) =>
      `/api/ApprovalTemplate/GetByTaskId?taskId=${taskId}&branchId=${branchId}`,
    save: '/api/ApprovalTemplate/Save',
  },

  // Self Service - Loan Request
  loanRequest: {
    getAll: (pageNumber: number, pageSize: number, branchId: number, codeType: string) =>
      `/api/ApiLoanRequest/GetAllLoanRequests/${pageNumber}/${pageSize}/${branchId}?codeType=${encodeURIComponent(codeType)}`,
    getById: (id: number, branchId: number) => `/api/ApiLoanRequest/GetLoanRequestById/${id}/${branchId}`,
    getLoanTypes: '/api/ApiLoanRequest/GetLoanTypes',
    getStatusLookups: '/api/ApiLoanRequest/GetStatusLookups',
    getActiveEmployees: (branchId: number) => `/api/ApiLoanRequest/GetActiveEmployees/${branchId}`,
    getDepartments: (branchId: number) => `/api/ApiLoanRequest/GetDepartments/${branchId}`,
    getAccounts: (branchId: number) => `/api/ApiLoanRequest/GetAccounts/${branchId}`,
    create: '/api/ApiLoanRequest/CreateLoanRequest',
    update: '/api/ApiLoanRequest/UpdateLoanRequest',
    delete: (id: number) => `/api/ApiLoanRequest/DeleteLoanRequest/${id}`,
  },

  // Self Service - Leave Request
  leaveRequest: {
    getAll: (branchId: number, pageNumber: number, pageSize: number, codeType?: string) => {
      const base = `/api/ApiLeaveRequest/GetAllLeaveRequests/${branchId}/${pageNumber}/${pageSize}`
      return codeType ? `${base}?codeType=${encodeURIComponent(codeType)}` : base
    },
    getById: (id: number, branchId: number) =>
      `/api/ApiLeaveRequest/GetLeaveRequestById/${id}/${branchId}`,
    getLeaveTypes: '/api/ApiLeaveRequest/GetLeaveTypes',
    getStatusLookups: '/api/ApiLeaveRequest/GetStatusLookups',
    getActiveEmployees: (branchId: number) => `/api/ApiLeaveRequest/GetActiveEmployees/${branchId}`,
    getEmployeeContract: (empId: number, branchId: number) =>
      `/api/ApiLeaveRequest/GetEmployeeContract/${empId}/${branchId}`,
    create: '/api/ApiLeaveRequest/CreateLeaveRequest',
    update: '/api/ApiLeaveRequest/UpdateLeaveRequest',
    delete: (id: number) => `/api/ApiLeaveRequest/DeleteLeaveRequest/${id}`,
  },

  // Self Service - Expense Claim Request
  expenseClaim: {
    getAll: (branchId: number, pageNumber: number, pageSize: number) =>
      `/api/ExpenseClaimRequest/GetAllexpenseClaim/${branchId}/${pageNumber}/${pageSize}`,
    getById: (id: number, branchId: number) =>
      `/api/ExpenseClaimRequest/GetAllexpenseClaimbyId/${id}/${branchId}`,
    getLines: (branchId: number) => `/api/ExpenseClaimRequest/GetAllexpenseClaimLines/${branchId}`,
    getLinesById: (id: number, branchId: number) =>
      `/api/ExpenseClaimRequest/GetAllexpenseClaimLinesById/${id}/${branchId}`,
    getDepartments: (branchId: number) => `/api/ExpenseClaimRequest/GetDepartment/${branchId}`,
    getEmployees: (branchId: number) => `/api/ExpenseClaimRequest/GetAllEmployee/${branchId}`,
    getTaxRates: (branchId: number) => `/api/ExpenseClaimRequest/GetTaxrates/${branchId}`,
    /** No query/body params; returns expense type lookups (id, lookupNameEn, lookupNameAr, …) */
    getExpenseTypes: '/api/ExpenseClaimRequest/GetExpenseTypes',
    save: '/api/ExpenseClaimRequest/SaveExpenseClaim',
    update: '/api/ExpenseClaimRequest/UpdateExpenseClaim',
    delete: (id: number) => `/api/ExpenseClaimRequest/DeleteExpenseClaim/${id}`,
  },

  // Lookup Management endpoints
  lookupManagement: {
    getAllCodeTypes: '/api/ApiLookup/GetAllCodeTypes',
    getCodeTypeById: (id: number) => `/api/ApiLookup/GetCodeTypeById/${id}`,
    getAllLookups: '/api/ApiLookup/GetAllLookups',
    getLookupsById: (lookupCodeId: number, parentId?: number) => {
      const url = `/api/ApiLookup/GetLookupsById/${lookupCodeId}`
      if (parentId !== undefined) {
        return `${url}?parentId=${parentId}`
      }
      return url
    },
    formSave: '/api/ApiLookup/FormSave',
  },

  // Tax Rate (VAT settings)
  taxRate: {
    getAll: (branchId: number, pageNumber: number, pageSize: number) =>
      `/api/TaxRate/GetAllTaxRates/${branchId}/${pageNumber}/${pageSize}`,
    getById: (id: number, branchId: number) =>
      `/api/TaxRate/GetTaxRateById/${id}/${branchId}`,
    save: '/api/TaxRate/SaveTaxRate',
    update: '/api/TaxRate/UpdateTaxRate',
    delete: (id: number) => `/api/TaxRate/DeleteTaxRate/${id}`,
  },

  // Branch management endpoints
  branches: {
    getAll: '/api/ApiBranch/GetAllBranches',
    getById: (id: number) => `/api/ApiBranch/GetBranchById/${id}`,
    save: '/api/ApiBranch/SaveBranch',
    update: '/api/ApiBranch/UpdateBranch',
    delete: (id: number) => `/api/ApiBranch/DeleteBranch/${id}`,
    getCountries: '/api/ApiBranch/GetCountryLookups',
    getCities: (countryId?: number) => {
      const base = '/api/ApiBranch/GetCityLookups'
      if (countryId !== undefined) {
        return `${base}?countryId=${countryId}`
      }
      return base
    },
  },

  // Inventory - Warehouses
  warehouse: {
    getAll: (branchId: number) => `/api/Warehouse/GetAllWarehouses?branchId=${branchId}`,
    getById: (id: number) => `/api/Warehouse/GetWarehouseById/${id}`,
    create: '/api/Warehouse/CreateWarehouse',
    update: '/api/Warehouse/UpdateWarehouse',
    delete: (id: number) => `/api/Warehouse/DeleteWarehouse/${id}`,
  },

  // Inventory - Item Categories
  itemCategories: {
    getAll: (branchId: number) =>
      `/api/ApiItemCategories/GetAllItemCategory/${branchId}`,
    getSubCategoriesByCategoryId: (catId: number) =>
      `/api/ApiItemCategories/GetItemSubCategoryByCategoryId/${catId}`,
    formSave: '/api/ApiItemCategories/FormSave',
  },

  // Inventory - Items
  items: {
    getAll: (branchId: number, pageNumber: number, pageSize: number) =>
      `/api/ApiItem/GetAllItems/${branchId}/${pageNumber}/${pageSize}`,
    getById: (id: number, branchId: number) => `/api/ApiItem/GetItemsById/${id}/${branchId}`,
    getAllItemCategory: (branchId: number) => `/api/ApiItemCategories/GetAllItemCategory/${branchId}`,
    getAllItemSubCategory: (branchId: number) => `/api/ApiItem/GetAllItemSubCategory/${branchId}`,
    getMaterialTypesLookups: '/api/ApiItem/GetMaterialTypesLookups',
    getMaterialUOMsLookups: '/api/ApiItem/GetMaterialUOMsLookups',
    getPurchaseTaxes: (branchId: number) => `/api/ApiItem/GetPurchaseTaxes?branchId=${branchId}`,
    getSalesTaxes: (branchId: number) => `/api/ApiItem/GetSalesTaxes?branchId=${branchId}`,
    save: '/api/ApiItem/Save2',
    update: '/api/ApiItem/Update2',
  },

  // Inventory - Item Batches
  batchItem: {
    getAll: (branchId: number, pageNumber: number, pageSize: number) =>
      `/api/BatchItem/GetAllBatchItems?branchId=${branchId}&pageNumber=${pageNumber}&pageSize=${pageSize}`,
    getById: (id: number) => `/api/BatchItem/GetBatchItemById/${id}`,
    /** Warehouse lines for a batch (edit form opening stock grid) */
    getWhItems: (batchId: number) => `/api/BatchItem/GetWhItems?batchId=${batchId}`,
    save: '/api/BatchItem/Save',
    update: '/api/BatchItem/Update',
  },

  // Report endpoints
  reports: {
    finance: '/reports/finance',            // GET - Financial reports
    hr: '/reports/hr',                      // GET - HR reports
    custom: '/reports/custom',              // POST - Generate custom report
    customerLedger: (fromDate: string, toDate: string, branchId: number, pageNumber: number, pageSize: number, customerId?: number) => {
      const base = `/api/LedgerReport/GetCustomerLedgerReport/${pageNumber}/${pageSize}`
      const params = new URLSearchParams()
      params.append('fromDate', fromDate)
      params.append('toDate', toDate)
      params.append('branchId', String(branchId))
      // Always include customerId - send 0 if not provided (for "All Customers")
      params.append('customerId', String(customerId !== undefined ? customerId : 0))
      return `${base}?${params.toString()}`
    },
    accountLedger: (fromDate: string, toDate: string, branchId: number, pageNumber: number, pageSize: number, accountId?: number) => {
      const base = `/api/LedgerReport/GetAccountLedgerReport/${pageNumber}/${pageSize}`
      const params = new URLSearchParams()
      params.append('fromDate', fromDate)
      params.append('toDate', toDate)
      params.append('branchId', String(branchId))
      // Always include accountId - send 0 if not provided (for "All Accounts")
      params.append('accountId', String(accountId !== undefined ? accountId : 0))
      return `${base}?${params.toString()}`
    },
    vendorLedger: (fromDate: string, toDate: string, branchId: number, pageNumber: number, pageSize: number, vendorId?: number) => {
      const base = `/api/LedgerReport/GetVendorLedgerReport/${pageNumber}/${pageSize}`
      const params = new URLSearchParams()
      params.append('fromDate', fromDate)
      params.append('toDate', toDate)
      params.append('branchId', String(branchId))
      // Always include vendorId - send 0 if not provided (for "All Vendors")
      params.append('vendorId', String(vendorId !== undefined ? vendorId : 0))
      return `${base}?${params.toString()}`
    },
    trialBalance: (fromDate: string, toDate: string, branchId: number, pageNumber: number, pageSize: number) => {
      const base = `/api/LedgerReport/GetTrialBalance/${pageNumber}/${pageSize}`
      const params = new URLSearchParams()
      params.append('fromDate', fromDate)
      params.append('toDate', toDate)
      params.append('branchId', String(branchId))
      return `${base}?${params.toString()}`
    },
    agedReceivables: (agingDate: string, branchId: number, pageNumber: number, pageSize: number, customerId?: number) => {
      const base = `/api/Aging/GetAgeReceivableReport/${pageNumber}/${pageSize}`
      const params = new URLSearchParams()
      params.append('agingDate', agingDate)
      params.append('branchId', String(branchId))
      // Always include customerId - send 0 if not provided (for "All Customers")
      params.append('customerId', String(customerId !== undefined ? customerId : 0))
      return `${base}?${params.toString()}`
    },
    agedPayables: (agingDate: string, branchId: number, pageNumber: number, pageSize: number, vendorId?: number) => {
      const base = `/api/Aging/GetAgePayableReport/${pageNumber}/${pageSize}`
      const params = new URLSearchParams()
      params.append('agingDate', agingDate)
      params.append('branchId', String(branchId))
      // Always include vendorId - send 0 if not provided (for "All Vendors")
      params.append('vendorId', String(vendorId !== undefined ? vendorId : 0))
      return `${base}?${params.toString()}`
    },
    vatReport: (fromDate: string, toDate: string, branchId: number, pageNumber: number, pageSize: number) => {
      const base = `/api/VatReport/GetVatReport/${pageNumber}/${pageSize}`
      const params = new URLSearchParams()
      params.append('fromDate', fromDate)
      params.append('toDate', toDate)
      params.append('branchId', String(branchId))
      return `${base}?${params.toString()}`
    },
    printVatReport: (fromDate: string, toDate: string, branchId: number) => {
      const base = `/api/VatReport/PrintVatReport`
      const params = new URLSearchParams()
      params.append('fromDate', fromDate)
      params.append('toDate', toDate)
      params.append('branchId', String(branchId))
      return `${base}?${params.toString()}`
    },
    fixedAssetReport: (fromDate: string, toDate: string, branchId: number, pageNumber: number, pageSize: number) => {
      const base = `/api/FixedAsset/GetFixedAssetReport/${pageNumber}/${pageSize}`
      const params = new URLSearchParams()
      params.append('fromDate', fromDate)
      params.append('toDate', toDate)
      params.append('branchId', String(branchId))
      return `${base}?${params.toString()}`
    },
    printFixedAssetReport: (fromDate: string, toDate: string, branchId: number) => {
      const base = `/api/FixedAsset/PrintFixedAssetReport`
      const params = new URLSearchParams()
      params.append('fromDate', fromDate)
      params.append('toDate', toDate)
      params.append('branchId', String(branchId))
      return `${base}?${params.toString()}`
    },
  },

  // File upload endpoints
  files: {
    upload: '/files/upload',                // POST - Upload file
    download: (id: string) => `/files/${id}`, // GET - Download file
    delete: (id: string) => `/files/${id}`,   // DELETE - Delete file
  },

  // Account Management endpoints
  accountManagement: {
    getMaster: '/api/AccountManagement/GetMaster',
    getAllAccounts: (branchId: number) => `/api/AccountManagement/GetAllAccounts/${branchId}`,
    getAccountSettingById: (id: number, branchId: number) => 
      `/api/AccountManagement/GetAccountSettingById/${id}/${branchId}`,
    saveAccountSetting: '/api/AccountManagement/SaveAccountSetting',
  },

  // Voucher/General Entry endpoints
  vouchers: {
    getVoucherTypes: '/api/Voucher/GetVoucherTypes',
    getAll: (branchId: number, pageNumber: number, pageSize: number) =>
      `/api/Voucher/GetAllVoucher/${branchId}/${pageNumber}/${pageSize}`,
    getAllLines: (branchId: number) => `/api/Voucher/GetAllVoucherLines/${branchId}`,
    getById: (id: number, branchId: number) => `/api/Voucher/GetVoucherById/${id}/${branchId}`,
    getLinesByVoucherId: (id: number, branchId: number) =>
      `/api/Voucher/GetVoucherLinesByVoucherId/${id}/${branchId}`,
    getLineById: (id: number, branchId: number) => `/api/Voucher/GetVoucherLineById/${id}/${branchId}`,
    create: '/api/Voucher/CreateVoucher',
    update: '/api/Voucher/UpdateVoucher',
  },

  // Customer Advance endpoints
  customerAdvances: {
    getAll: (branchId: number, pageNumber: number, pageSize: number) =>
      `/api/ApiCustomerAdvance/GetAllAdvance/${branchId}/${pageNumber}/${pageSize}`,
    getById: (branchId: number, id: number) =>
      `/api/ApiCustomerAdvance/GetAllAdvanceById/${branchId}/${id}`,
    getAllAccounts: (branchId: number) =>
      `/api/ApiCustomerAdvance/GetAllAccounts/${branchId}`,
    insert: '/api/ApiCustomerAdvance/Insert',
    update: '/api/ApiCustomerAdvance/Update',
    postData: '/api/ApiCustomerAdvance/PostData',
  },

  // Vendor Advance endpoints
  vendorAdvances: {
    getAll: (branchId: number, pageNumber: number, pageSize: number) =>
      `/api/ApiVendorAdvance/GetAllAdvance/${branchId}/${pageNumber}/${pageSize}`,
    getById: (branchId: number, id: number) =>
      `/api/ApiVendorAdvance/GetAllAdvanceById/${branchId}?id=${id}`,
    getAllAccounts: (branchId: number) =>
      `/api/ApiVendorAdvance/GetAllAccounts/${branchId}`,
    insert: '/api/ApiVendorAdvance/Insert',
    update: '/api/ApiVendorAdvance/Update',
    postData: '/api/ApiVendorAdvance/PostData',
  },

  // Sales Receipts endpoints
  receipts: {
    getAll: (branchId: number, pageNumber: number, pageSize: number) =>
      `/api/ApiReceipts/GetAllRecepit/${branchId}/${pageNumber}/${pageSize}`,
    getById: (branchId: number, id: number) =>
      `/api/ApiReceipts/GetAllRecepitById/${branchId}?id=${id}`,
    getCustomerInvoices: (customerId: number) =>
      `/api/ApiReceipts/GetCustomerInvoices/${customerId}`,
    getCustomerAdvances: (customerId: number, receiptId: number) =>
      `/api/ApiReceipts/getCustomerAdvances/${customerId}/${receiptId}`,
    getCustomerAdvanceLinesByReceiptId: (receiptId: number) =>
      `/api/ApiReceipts/GetCustomerAdvanceLinesByReceiptId/${receiptId}`,
    getReceiptLinesByReceiptId: (receiptId: number) =>
      `/api/ApiReceipts/GetReceiptLinesByReceiptId/${receiptId}`,
    getAllAccounts: (branchId: number) =>
      `/api/ApiReceipts/GetAllAccounts/${branchId}`,
    insert: '/api/ApiReceipts/Insert',
    update: '/api/ApiReceipts/Update',
  },

  // Receipt Return endpoints
  receiptReturn: {
    getAll: (branchId: number, pageNumber: number, pageSize: number) =>
      `/api/ReceiptReturn/GetReceiptReturns/${branchId}/${pageNumber}/${pageSize}`,
    getInvoicesByCustomerId: (customerId: number) =>
      `/api/ReceiptReturn/GetInvoicesByCustomerId?customerId=${customerId}`,
    getInsertData: (branchId: number, invoiceId: number) =>
      `/api/ReceiptReturn/GetInsertData/${branchId}?invoiceId=${invoiceId}`,
    save: '/api/ReceiptReturn',
    update: '/api/ReceiptReturn',
  },

  // Payment Return endpoints
  paymentReturn: {
    getAll: (branchId: number, pageNumber: number, pageSize: number) =>
      `/api/PaymentReturn/GetPaymentReturns/${branchId}/${pageNumber}/${pageSize}`,
    getById: (id: number, branchId: number) =>
      `/api/PaymentReturn/GetPaymentReturnById/${id}/${branchId}`,
    getBillsByVendorId: (vendorId: number) =>
      `/api/PaymentReturn/GetBillsByVendorId?vendorId=${vendorId}`,
    getInsertData: (branchId: number, billId: number) =>
      `/api/PaymentReturn/GetInsertData/${branchId}?billId=${billId}`,
    save: '/api/PaymentReturn',
    update: '/api/PaymentReturn',
    delete: (id: number) => `/api/PaymentReturn/${id}`,
  },

  // Vendor management endpoints
  vendors: {
    getAll: (branchId: number, pageNumber: number, pageSize: number) =>
      `/api/Vendor/GetAllVendors/${branchId}/${pageNumber}/${pageSize}`,
    getById: (id: number, branchId: number) =>
      `/api/Vendor/GetVendorById/${id}/${branchId}`,
    save: '/api/Vendor/SaveVendor',
    update: '/api/Vendor/UpdateVendor',
    delete: (id: number, ipAddress: string) =>
      `/api/Vendor/DeleteVendor/${id}/${ipAddress}`,
    getStates: '/api/ApiBranch/GetStateLookups',
    getCountries: '/api/ApiBranch/GetCountryLookups',
    getCities: (countryId?: number) => {
      const base = '/api/ApiBranch/GetCityLookups'
      if (countryId !== undefined) {
        return `${base}?countryId=${countryId}`
      }
      return base
    },
  },

  // Purchase Bills endpoints
  bills: {
    getAll: (branchId: number, pageNumber: number, pageSize: number) =>
      `/api/ApiBills/GetAllBillList/${branchId}/${pageNumber}/${pageSize}`,
    getById: (id: number) =>
      `/api/ApiBills/GetBillById/${id}`,
    getBillLines: (branchId: number, id: number) =>
      `/api/ApiBills/GetBillLinesByBillId/${branchId}/${id}`,
    getAllVendors: (branchId: number) => `/api/ApiBills/GetAllVendors/${branchId}`,
    getAllBillLines: (branchId: number) => `/api/ApiBills/GetAllBillLines/${branchId}`,
    getProject: (branchId: number) => `/api/ApiBills/GetProject/${branchId}`,
    getItemList: (branchId: number) => `/api/ApiBills/GetItemList/${branchId}`,
    getTaxRates: (branchId: number) => `/api/ApiBills/GetTaxRates/${branchId}`,
    getCostLocation: '/api/ApiBills/GetCostLocation',
    getProfitCenter: '/api/ApiBills/GetProfitCenter',
    getDepartments: '/api/ApiBills/GetDepartments',
    getPaymentTerms: '/api/ApiBills/GetlookuppaymentTerms',
    getAllAccounts: (branchId: number) => `/api/ApiBills/GetAllAccounts/${branchId}`,
    insert: '/api/ApiBills/Insert',
    update: '/api/ApiBills/Update',
  },

  // Vendor Refunds (Credit Notes) endpoints
  refunds: {
    getAll: (branchId: number, pageNumber: number, pageSize: number) =>
      `/api/ApiRefunds/GetAllRefunds/${branchId}/${pageNumber}/${pageSize}`,
    getAllVendors: (branchId: number) => `/api/ApiRefunds/GetAllVendors/${branchId}`,
    getAllRefundLines: (branchId: number) => `/api/ApiRefunds/GetAllRefundLines/${branchId}`,
    getAllBills: (branchId: number) => `/api/ApiRefunds/GetAllBills/${branchId}`,
    getBillLines: (branchId: number, billId: number) =>
      `/api/ApiRefunds/GetBillsLines/${billId}/${branchId}`,
    insert: '/api/ApiRefunds/Insert',
    update: '/api/ApiRefunds/Update',
  },

  // Purchase Payments endpoints
  payments: {
    getAll: (branchId: number, pageNumber: number, pageSize: number) =>
      `/api/Payments/GetAllPayments/${branchId}/${pageNumber}/${pageSize}`,
    getById: (branchId: number, id: number) =>
      `/api/Payments/GetAllPaymentsById/${branchId}?id=${id}`,
    getAllBills: (branchId: number) =>
      `/api/Payments/GetAllBills/${branchId}`,
    getAllAdvance: (branchId: number) =>
      `/api/Payments/GetAllAdvance/${branchId}`,
    getVendorsBills: (vendorId: number) =>
      `/api/Payments/GetVendorsBills/${vendorId}`,
    getVendorsAdvance: (vendorId: number, advanceId: number) =>
      `/api/Payments/GetVendorsAdvance/${vendorId}/${advanceId}`,
    getVendorAdvanceLineByAdvanceId: (advanceId: number) =>
      `/api/Payments/GetVendorAdvanceLineByAdvanceId/${advanceId}`,
    getPaymentLinesByPaymentId: (paymentId: number) =>
      `/api/Payments/GetPaymentLinesByPaymentId/${paymentId}`,
    getAllAccounts: (branchId: number) =>
      `/api/Payments/GetAllAccounts/${branchId}`,
    insert: '/api/Payments/Insert',
    update: '/api/Payments/Update',
  },

  // Fixed Asset endpoints
  fixedAssets: {
    getAll: (branchId: number, pageNumber: number, pageSize: number) =>
      `/api/FixedAsset/GetAllAssets/${branchId}/${pageNumber}/${pageSize}`,
    getById: (id: number, branchId: number) =>
      `/api/FixedAsset/GetAssetById/${id}/${branchId}`,
    getByType: (id: number, branchId: number) =>
      `/api/FixedAsset/GetAssetsByType/${id}/${branchId}`,
    save: '/api/FixedAsset/Save',
    update: '/api/FixedAsset/Update',
    delete: (id: number) => `/api/FixedAsset/Delete/${id}`,
    getDepMethods: '/api/FixedAsset/GetDepMethods',
    getDepreciationMethodLookups: '/api/FixedAsset/GetDepreciationMethodLookups',
    getAssetCategoryLookups: '/api/FixedAsset/GetAssetCategoryLookups',
    getAssetTypes: '/api/FixedAsset/GetAssetTypes',
    depreciationRun: (branchId: number, monthYear: string) =>
      `/api/FixedAsset/DepreciationRun?branchId=${branchId}&MonthYear=${monthYear}`,
    // Asset Disposal endpoints
    disposal: {
      getAll: (branchId: number, pageNumber: number, pageSize: number) =>
        `/api/FixedAsset/GetAssetDisposals/${branchId}/${pageNumber}/${pageSize}`,
      save: '/api/FixedAsset/AssetDisposal',
    },
  },

  // GOSI Setting endpoints
  gosiSetting: {
    get: (branchId: number) => `/api/GosiSetting/GetGosi?branchId=${branchId}`,
    addOrUpdate: '/api/GosiSetting/AddOrUpdateGosi',
  },
} as const

// Helper function to build URL with query parameters
export const buildUrl = (endpoint: string, params?: Record<string, any>): string => {
  if (!params) return endpoint
  
  const url = new URL(endpoint, 'http://localhost')
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.append(key, String(value))
    }
  })
  
  return url.pathname + url.search
}

// Common query parameters for list endpoints
export interface ListParams {
  page?: number
  pageSize?: number
  search?: string
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  filters?: Record<string, any>
}

// Helper to build list URLs with common parameters
export const buildListUrl = (endpoint: string, params?: ListParams): string => {
  return buildUrl(endpoint, params)
}
